import { Container, ContentLayout, ModuleFillType, ModuleFlowDirection } from '@/common/types';
import classNames from 'classnames';
import { useMemo } from 'react';
import {
  CONTAINER_OPTIONS,
  CONTENT_LAYOUT_OPTIONS,
  MODULE_FILL_TYPE_OPTIONS,
  MODULE_FLOW_DIRECTION_OPTIONS,
} from '../constants/module';

export interface UseModuleStylesProps {
  container?: Container;
  fillType?: ModuleFillType;
  gradientFillToken?: string;
  flowDirection?: ModuleFlowDirection;
  layout?: ContentLayout;
  moduleClassName: string;
  styles: Record<string, string>;
  isFirstModule?: boolean;
}

export interface UseModuleStylesReturn {
  sectionStyles: string;
  moduleBaseStyles: string;
}

/**
 * Hook to get the styles for a module
 * @param props - The props for the module
 * @returns The styles for the module
 */
export const useModuleStyles = ({
  // container type (contained or uncontained)
  container = CONTAINER_OPTIONS.CONTAINED,
  // fill type (base, gradient, raised, no-fill)
  fillType = MODULE_FILL_TYPE_OPTIONS.BASE,
  // gradient fill token
  gradientFillToken,
  // module flow direction (column or row)
  flowDirection = MODULE_FLOW_DIRECTION_OPTIONS.ROW,
  // layout for content (leading or trailing)
  layout = CONTENT_LAYOUT_OPTIONS.CONTENT_TRAILING,
  // module class name
  moduleClassName,
  // checks for extra padding on the first module
  isFirstModule = false,
  // scss module styles object specific to the module
  styles,
}: UseModuleStylesProps): UseModuleStylesReturn => {
  return useMemo(() => {
    const isGradientFill = fillType === MODULE_FILL_TYPE_OPTIONS.GRADIENT;

    // Module Styling
    const sectionStyles = classNames(
      moduleClassName,
      styles['section_container'],
      styles[`module_block__${container}`],
      styles[`section_bg_fill__${fillType}`],
      isGradientFill ? styles[`gradient-${gradientFillToken}`] : '',
      isFirstModule && styles.with_navbar,
    );

    const moduleBaseStyles = classNames(
      styles['module-base'],
      styles[`direction__${flowDirection}`],
      styles[`layout__${layout}`],
    );

    return {
      sectionStyles,
      moduleBaseStyles,
    };
  }, [
    container,
    fillType,
    gradientFillToken,
    flowDirection,
    layout,
    isFirstModule,
    moduleClassName,
    styles,
  ]);
};
